import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input, Card, Empty, Typography, Avatar, Popconfirm, Tooltip } from 'antd';
import {
  MessageOutlined,
  CloseOutlined,
  SendOutlined,
  ArrowLeftOutlined,
  UserOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { api, User, ChatMsg, isAdminRole } from '../api';
import { t } from '../i18n';

const fmt = (u: number): string => {
  const d = new Date(u * 1000);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/** Floating support-chat widget. Client (or an impersonating admin) sees a single
 * conversation; a real admin sees a list of client threads. Realtime over WS. */
export function ChatWidget({ user, impersonating }: { user: User; impersonating?: boolean }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  // Admin (not impersonating) picks a thread; everyone else has one implicit thread.
  const adminMode = isAdminRole(user.role) && !impersonating;
  const actingAsAdmin = adminMode || !!impersonating;
  const [activeThread, setActiveThread] = useState<string | null>(null);

  // The thread we're viewing: admin → chosen; else the implicit (own) thread.
  const threadId = adminMode ? activeThread : user.id;

  const unread = useQuery({ queryKey: ['chat-unread'], queryFn: api.chatUnread, refetchInterval: 20_000 });
  const threads = useQuery({
    queryKey: ['chat-threads'],
    queryFn: api.chatThreads,
    enabled: adminMode && open,
    refetchInterval: open ? 10_000 : false,
  });
  const messages = useQuery({
    queryKey: ['chat-messages', threadId],
    queryFn: () => api.chatMessages(adminMode ? threadId! : undefined),
    enabled: open && !!threadId,
  });

  const send = useMutation({
    mutationFn: (body: string) => api.chatSend(body, adminMode ? threadId! : undefined),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-messages', threadId] });
      qc.invalidateQueries({ queryKey: ['chat-unread'] });
    },
  });

  // Admin: wipe the current client's thread. The server broadcasts a 'cleared'
  // event so every open window (incl. the client's) empties it too.
  const clearChat = useMutation({
    mutationFn: () => api.chatClear(threadId!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['chat-messages', threadId] });
      qc.invalidateQueries({ queryKey: ['chat-unread'] });
      qc.invalidateQueries({ queryKey: ['chat-threads'] });
    },
  });

  // Realtime: one socket for the widget's lifetime; refresh on any inbound message.
  useEffect(() => {
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    let ws: WebSocket | null = null;
    let closed = false;
    const connect = (): void => {
      ws = new WebSocket(`${proto}//${location.host}/api/chat/ws`);
      ws.onmessage = (ev) => {
        try {
          const m = JSON.parse(ev.data) as {
            type: string;
            message?: ChatMsg;
            threadClientId?: string;
          };
          if (m.type === 'cleared') {
            // A thread was wiped — refresh it (and the lists) so open windows empty it.
            qc.invalidateQueries({ queryKey: ['chat-messages', m.threadClientId] });
            qc.invalidateQueries({ queryKey: ['chat-unread'] });
            qc.invalidateQueries({ queryKey: ['chat-threads'] });
            return;
          }
          if (m.type !== 'message' || !m.message) return;
          qc.invalidateQueries({ queryKey: ['chat-messages', m.message.threadClientId] });
          qc.invalidateQueries({ queryKey: ['chat-unread'] });
          qc.invalidateQueries({ queryKey: ['chat-threads'] });
        } catch {
          /* ignore */
        }
      };
      ws.onclose = () => {
        if (!closed) setTimeout(connect, 3000); // auto-reconnect
      };
    };
    connect();
    return () => {
      closed = true;
      ws?.close();
    };
  }, [qc]);

  // Mark read + scroll to bottom when a thread is open.
  const bottom = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && threadId && messages.data) {
      api.chatRead(adminMode ? threadId : undefined).then(() =>
        qc.invalidateQueries({ queryKey: ['chat-unread'] }),
      );
      bottom.current?.scrollIntoView({ block: 'end' });
    }
  }, [open, threadId, messages.data, adminMode, qc]);

  const [draft, setDraft] = useState('');
  const submit = (): void => {
    const b = draft.trim();
    if (!b || !threadId) return;
    setDraft('');
    send.mutate(b);
  };

  const unreadCount = unread.data?.unread ?? 0;

  if (!open) {
    // Fixed WRAPPER (not the button) so the red count badge anchors to the icon.
    return (
      <div style={{ position: 'fixed', right: 16, bottom: 16, zIndex: 1500 }}>
        <Badge count={unreadCount} overflowCount={99} offset={[-2, 2]}>
          <Button
            type="primary"
            shape="circle"
            icon={<MessageOutlined />}
            onClick={() => setOpen(true)}
            style={{ width: 40, height: 40, boxShadow: '0 4px 14px rgba(0,0,0,0.3)' }}
            aria-label={t('chatWithSupport')}
          />
        </Badge>
      </div>
    );
  }

  const showThreadList = adminMode && !activeThread;
  // Admin conversation header shows WHO the chat is with (the client's name).
  const activeThreadName =
    adminMode && activeThread
      ? threads.data?.threads.find((th) => th.clientId === activeThread)?.clientName ??
        activeThread.slice(0, 8)
      : null;

  return (
    <Card
      size="small"
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {adminMode && activeThread && (
            <ArrowLeftOutlined onClick={() => setActiveThread(null)} style={{ cursor: 'pointer' }} />
          )}
          <MessageOutlined />
          {showThreadList
            ? t('chatThreads')
            : activeThreadName
              ? t('chatWithName', { name: activeThreadName })
              : t('chatTitle')}
        </span>
      }
      extra={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {adminMode && activeThread && (
            <Popconfirm
              title={t('chatClearConfirm')}
              okText={t('chatClear')}
              okButtonProps={{ danger: true, loading: clearChat.isPending }}
              onConfirm={() => clearChat.mutate()}
            >
              <Tooltip title={t('chatClear')}>
                <DeleteOutlined style={{ cursor: 'pointer' }} />
              </Tooltip>
            </Popconfirm>
          )}
          <CloseOutlined onClick={() => setOpen(false)} style={{ cursor: 'pointer' }} />
        </span>
      }
      style={{ position: 'fixed', right: 24, bottom: 24, zIndex: 1500, width: 360, boxShadow: '0 8px 28px rgba(0,0,0,0.4)' }}
      styles={{ body: { padding: 0, display: 'flex', flexDirection: 'column', height: 460 } }}
    >
      {showThreadList ? (
        <ThreadList
          threads={threads.data?.threads ?? []}
          onOpen={(id) => setActiveThread(id)}
        />
      ) : (
        <>
          <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(messages.data?.messages ?? []).length === 0 && (
              <div style={{ margin: 'auto' }}>
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('chatEmpty')} />
              </div>
            )}
            {(messages.data?.messages ?? []).map((m) => {
              const mine = actingAsAdmin ? isAdminRole(m.senderRole as 'admin') : m.senderRole === 'client';
              return <Bubble key={m.id} msg={m} mine={mine} />;
            })}
            <div ref={bottom} />
          </div>
          <div style={{ display: 'flex', gap: 6, padding: 8, borderTop: '1px solid rgba(127,127,127,0.2)' }}>
            <Input.TextArea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={t('chatPlaceholder')}
              autoSize={{ minRows: 1, maxRows: 4 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            <Button type="primary" icon={<SendOutlined />} loading={send.isPending} onClick={submit} disabled={!draft.trim()} />
          </div>
        </>
      )}
    </Card>
  );
}

function ThreadList({
  threads,
  onOpen,
}: {
  threads: import('../api').ChatThread[];
  onOpen: (clientId: string) => void;
}) {
  if (threads.length === 0) {
    return <div style={{ margin: 'auto', padding: 24 }}><Empty description={t('chatNoThreads')} /></div>;
  }
  return (
    <div style={{ overflowY: 'auto', flex: 1 }}>
      {threads.map((th) => (
        <div
          key={th.clientId}
          onClick={() => onOpen(th.clientId)}
          style={{ display: 'flex', gap: 10, padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid rgba(127,127,127,0.12)', alignItems: 'center' }}
        >
          <Badge count={th.unread} size="small">
            <Avatar icon={<UserOutlined />} size="small" />
          </Badge>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography.Text strong ellipsis>{th.clientName || th.clientId.slice(0, 8)}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>{fmt(th.lastAt)}</Typography.Text>
            </div>
            <Typography.Text type="secondary" ellipsis style={{ fontSize: 12, display: 'block' }}>
              {th.lastSenderRole !== 'client' ? `${t('chatYou')}: ` : ''}{th.lastBody}
            </Typography.Text>
          </div>
        </div>
      ))}
    </div>
  );
}

function Bubble({ msg, mine }: { msg: ChatMsg; mine: boolean }) {
  return (
    <div style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '80%' }}>
      {!mine && (
        <Typography.Text type="secondary" style={{ fontSize: 10, marginLeft: 8 }}>
          {msg.senderName || msg.senderRole}
        </Typography.Text>
      )}
      <div
        style={{
          background: mine ? '#5b8def' : 'rgba(127,127,127,0.16)',
          color: mine ? '#fff' : undefined,
          borderRadius: 12,
          padding: '6px 10px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {msg.body}
      </div>
      <div style={{ textAlign: mine ? 'right' : 'left' }}>
        <Typography.Text type="secondary" style={{ fontSize: 10 }}>{fmt(msg.createdAt)}</Typography.Text>
      </div>
    </div>
  );
}
